import React, { useSyncExternalStore } from 'react';
import { connect } from 'react-redux';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { openDialog } from '../../../base/dialog/actions';
import { translate } from '../../../base/i18n/functions';
import { IconCalendar } from '../../../base/icons/svg';
import AbstractButton from '../../../base/toolbox/components/AbstractButton';
import { formatScheduledTime, getScheduledRequestView, subscribeScheduledRequest } from '../../../interpreter-queue/scheduledRequest';
import { getPersistentJson } from '../../../vrs-auth/storage';
import { hasClientCallActions } from '../../clientCallActions.web';
import ScheduleInterpreterDialog from './ScheduleInterpreterDialog';
/**
 * The client's in-call "Schedule an interpreter request for later" on web
 * (plan #75; native: ScheduleInterpreterButton in the More sheet). It is on
 * the bar when every call action fits, else last in More (Toolbox,
 * splitClientCallBar), and opens ScheduleInterpreterDialog. Once a
 * request is scheduled it reads "Scheduled 3:30 PM" and opens the same dialog
 * to cancel it. It replaced the Schedule in the floating room pill.
 */
class ScheduleInterpreterButton extends AbstractButton {
    constructor() {
        super(...arguments);
        this.accessibilityLabel = 'toolbar.accessibilityLabel.scheduleInterpreter';
        this.icon = IconCalendar;
        this.label = 'toolbar.scheduleInterpreter';
        this.tooltip = 'toolbar.scheduleInterpreter';
    }
    /**
     * The label, with the time once scheduled. The time goes in as a value:
     * as part of the key, i18next would read "3:30" as a namespace.
     *
     * @returns {string}
     */
    _getLabel() {
        this.labelProps = { time: this.props._scheduledAt };
        return this.props._scheduledAt ? 'toolbar.scheduledInterpreter' : this.label;
    }
    /**
     * Pressed while a request is scheduled.
     *
     * @returns {boolean}
     */
    _isToggled() {
        return Boolean(this.props._scheduledAt);
    }
    /**
     * Opens the schedule dialog.
     *
     * @private
     * @returns {void}
     */
    _handleClick() {
        sendAnalytics(createToolbarEvent('schedule.interpreter'));
        this.props.dispatch(openDialog(ScheduleInterpreterDialog));
    }
}
/**
 * A signed-in client in a VRS call (not a VRI one, as native: VRI sessions are
 * booked in the VRI console).
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function canScheduleInterpreter(state) {
    const room = state['features/base/conference'].room;
    const call = getPersistentJson('vrs_active_call');
    return hasClientCallActions(state) && !(call?.callType === 'vri' && call.roomName === room);
}
/**
 * Maps state to props: who sees it.
 *
 * @param {IReduxState} state - The redux state.
 * @param {IProps} ownProps - The component's own props.
 * @returns {Object}
 */
function _mapStateToProps(state, ownProps) {
    return {
        visible: ownProps.visible ?? canScheduleInterpreter(state)
    };
}
// The toolbox passes its item props through; _scheduledAt is added here.
const ConnectedScheduleInterpreterButton = translate(connect(_mapStateToProps)(ScheduleInterpreterButton));
/**
 * The More item, following the schedule (scheduledRequest.ts keeps it outside
 * redux) so its label changes the moment the server answers.
 *
 * @param {Object} props - The toolbox's props for the item.
 * @returns {JSX.Element}
 */
export default function ScheduleInterpreterMenuItem(props) {
    const request = useSyncExternalStore(subscribeScheduledRequest, getScheduledRequestView, getScheduledRequestView);
    const scheduled = request.phase === 'scheduled' || request.phase === 'cancelling';
    return (React.createElement(ConnectedScheduleInterpreterButton, { ...props, _scheduledAt: scheduled ? formatScheduledTime(request.scheduledStartAt) || undefined : undefined }));
}
